import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { ChevronDown } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQ = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openIndex, setOpenIndex] = useState(null);

  useEffect(() => {
    const fetchFAQs = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/faqs`);
        setFaqs(response.data);
      } catch (error) {
        console.error('Error fetching FAQs:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchFAQs();
  }, []);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div data-testid="faq-page" className="bg-[#0A0A0A] min-h-screen pt-20">
      <Navigation />
      
      {/* Hero */}
      <section className="py-24 px-6 bg-[#141414]">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Oswald'] text-5xl sm:text-6xl md:text-7xl text-[#F2EDE6] mb-4"
          >
            FREQUENTLY ASKED QUESTIONS
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[#A19D98] text-lg"
          >
            Everything you need to know about joining the Rally Family.
          </motion.p>
        </div>
      </section>

      {/* FAQ List */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto">
          {loading ? (
            <p className="text-[#A19D98] text-center">Loading FAQs...</p>
          ) : faqs.length === 0 ? (
            <p className="text-[#A19D98] text-center">No FAQs available yet.</p>
          ) : (
            faqs.map((faq, index) => (
              <motion.div
                key={faq.id || index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="border-b border-[#2A2A2A]"
              >
                <button
                  onClick={() => toggleFAQ(index)}
                  data-testid={`faq-question-${index}`}
                  className="w-full py-6 flex items-center justify-between text-left"
                >
                  <span className="font-['Oswald'] text-lg sm:text-xl text-[#F2EDE6] pr-8">
                    {faq.question}
                  </span>
                  <ChevronDown 
                    className={`w-5 h-5 text-[#E8651A] flex-shrink-0 transition-transform duration-300 ${
                      openIndex === index ? 'rotate-180' : ''
                    }`}
                  />
                </button>
                
                <motion.div
                  initial={false}
                  animate={{
                    height: openIndex === index ? 'auto' : 0,
                    opacity: openIndex === index ? 1 : 0
                  }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <p className="text-[#A19D98] pb-6 leading-relaxed">
                    {faq.answer}
                  </p>
                </motion.div>
              </motion.div>
            ))
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 px-6 bg-[#141414]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Oswald'] text-2xl sm:text-3xl text-[#F2EDE6] mb-4">
              STILL HAVE QUESTIONS?
            </h2>
            <p className="text-[#A19D98] mb-8">
              Reach out on Instagram or sign up for a rally and ask us in person!
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://www.instagram.com/revlinerally/"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
              >
                DM Us on Instagram
              </a>
              <a href="/rallies" className="btn-primary">
                View Rallies
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQ;
